import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { createTicket, updateTicket, getProjects, getConsultants, getAllUsers, getSubProjects, getProjectMembers, ensureRequestor, getGroups, uploadAttachment } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Select from '../components/Select';
import RichTextEditor from '../components/RichTextEditor';
import VoiceRecorder from '../components/VoiceRecorder';
import { HelpCircle, X } from 'lucide-react';

// Default description template pre-filled into the rich-text editor.
const DESCRIPTION_TEMPLATE = [
  '<p><strong>Issue Statement:</strong></p>',
  '<p><br></p>',
  '<p><strong>Issue Detailed Description:</strong></p>',
  '<p><br></p>',
  '<p><strong>Steps to Replicate the issue:</strong></p>',
  '<ol><li></li><li></li><li></li></ol>',
  '<p><strong>Current behavior:</strong></p>',
  '<p><br></p>',
  '<p><strong>Expected behavior:</strong></p>',
  '<p><br></p>',
  '<p><strong>Supporting Screenshots/documents/videos/dashboard links:</strong></p>',
  '<p><br></p>',
].join('');

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

const CATMAP = {
  'Zyra ERP': {
    Finance: ['Accounts Payable', 'Accounts Receivable', 'General Ledger', 'Fixed Assets', 'Budgeting', 'Tax Management'],
    HRMS: ['Employee Master', 'Attendance', 'Leave Management', 'Payroll Integration', 'Recruitment', 'Appraisal'],
    Payroll: ['Payroll Processing', 'Salary Revision', 'Compliance Reports', 'Loan & Advance', 'Full & Final'],
    Inventory: ['Stock Management', 'Item Master', 'Warehouse', 'Stock Transfer', 'Valuation'],
    Purchase: ['Purchase Order', 'Vendor Master', 'GRN', 'Purchase Return', 'Vendor Payment'],
    Sales: ['Sales Order', 'Customer Master', 'Delivery', 'Sales Return', 'Customer Receipt'],
    CRM: ['Lead Management', 'Opportunity', 'Customer Follow-up', 'Service Cases'],
    Production: ['BOM', 'Work Order', 'Production Entry', 'Scrap Management'],
    Quality: ['QC Checklist', 'Rejection', 'NCR', 'Audit'],
    Asset: ['Asset Master', 'Depreciation', 'Asset Transfer', 'Disposal'],
    Projects: ['Project Creation', 'Task Management', 'Resource Allocation', 'Project Billing'],
    Reports: ['MIS Reports', 'Custom Reports', 'Dashboard', 'Export Issues'],
    Admin: ['User Management', 'Roles & Permissions', 'Company Settings', 'Backup & Restore'],
  },
  'SAP ERP': {
    'SAP-FI': ['G/L Accounting', 'Accounts Payable', 'Accounts Receivable', 'Asset Accounting', 'Bank Accounting', 'Closing Activities'],
    'SAP-CO': ['Cost Center', 'Internal Orders', 'Profit Center', 'Product Costing', 'COPA'],
    'SAP-MM': ['Purchase Requisition', 'Purchase Order', 'GR/GI', 'Invoice Verification', 'Inventory Management'],
    'SAP-SD': ['Sales Order', 'Delivery', 'Billing', 'Pricing', 'Credit Management'],
    'SAP-PP': ['Production Order', 'BOM', 'Routing', 'MRP', 'Capacity Planning'],
    'SAP-HCM': ['Personnel Administration', 'Time Management', 'Payroll', 'Organizational Management'],
    'BASIS/ABAP': ['System Administration', 'Transport Management', 'User Administration', 'Performance', 'Custom Development'],
  },
  'Oracle ERP': {
    Financials: ['General Ledger', 'Payables', 'Receivables', 'Fixed Assets', 'Cash Management'],
    SCM: ['Purchasing', 'Inventory', 'Order Management', 'Shipping'],
    HCM: ['Core HR', 'Payroll', 'Benefits', 'Talent Management'],
    Projects: ['Project Costing', 'Project Billing', 'Project Management'],
  },
  'Custom ERP': {
    General: ['Feature Request', 'Bug Report', 'Performance Issue', 'Data Issue', 'Integration'],
    Reports: ['Custom Report', 'Export Issue', 'Dashboard'],
    Admin: ['User Access', 'Configuration', 'Upgrade'],
  },
  Infrastructure: {
    Server: ['Server Down', 'High CPU/Memory', 'Disk Space', 'OS Issues', 'Backup Failure'],
    Cloud: ['Cloud VM Issue', 'Storage', 'Network ACL', 'Cost Optimization'],
    Database: ['DB Performance', 'DB Connectivity', 'Replication', 'Backup & Recovery'],
    Security: ['Firewall', 'SSL Certificate', 'Vulnerability', 'Access Control'],
  },
  Network: {
    Connectivity: ['Internet Down', 'LAN Issue', 'VPN Issue', 'Wi-Fi Issue'],
    Configuration: ['Switch/Router Config', 'DNS/DHCP', 'IP Conflict', 'Bandwidth'],
    Monitoring: ['Alerts Setup', 'Network Scan', 'Latency Issue'],
  },
  Hardware: {
    'Desktop/Laptop': ['Not Booting', 'Hardware Failure', 'Peripheral Issue', 'Upgrade Request'],
    'Printer/Scanner': ['Not Printing', 'Connectivity', 'Driver Issue'],
    'Server Hardware': ['Disk Failure', 'RAM Issue', 'Power Supply', 'RAID'],
  },
  Software: {
    OS: ['Windows Issues', 'Linux Issues', 'Driver Problem', 'Activation'],
    'Office Suite': ['MS Office', 'Email Client', 'PDF Tools'],
    Antivirus: ['Virus Detection', 'Update Issue', 'Policy', 'Quarantine'],
    'Other Software': ['Installation', 'Licensing', 'Crash/Error', 'Update'],
  },
};

const ERP_SYSTEMS = ['Zyra ERP', 'SAP ERP', 'Oracle ERP', 'Custom ERP'];

const SAP_VERSIONS = [
  'SAP ECC 6.0',
  'SAP S/4HANA 1709',
  'SAP S/4HANA 1809',
  'SAP S/4HANA 1909',
  'SAP S/4HANA 2020',
  'SAP S/4HANA 2021',
  'SAP S/4HANA 2022',
  'SAP S/4HANA 2023',
  'SAP BTP',
];

const ERP_VERSIONS = {
  'Zyra ERP': ['Zyra 1.0', 'Zyra 2.0', 'Zyra 2.5', 'Zyra 3.0'],
  'Oracle ERP': ['Oracle EBS R12.1', 'Oracle EBS R12.2', 'Oracle Cloud Fusion'],
  'Custom ERP': ['v1', 'v2', 'v3', 'Other'],
};

const PRIORITY_MATRIX = {
  high: { high: 'critical', medium: 'high', low: 'medium' },
  medium: { high: 'high', medium: 'medium', low: 'low' },
  low: { high: 'medium', medium: 'low', low: 'low' },
};

// Default CC recipients pre-filled into "Email Notifications (CC)" on every
// create/update. They are editable chips — a user may remove either before
// submitting, and that address simply won't be copied for that action.
const DEFAULT_CC_EMAILS = [
  'ganeshbala@saiteck.in',
  'harims@saiteck.in',
];
// Merge any existing CC list with the defaults (case-insensitive, no dupes),
// keeping the defaults present unless the user explicitly removes them.
const withDefaultCc = (existing = []) => {
  const seen = new Set(existing.map(e => e.toLowerCase()));
  return [...existing, ...DEFAULT_CC_EMAILS.filter(e => !seen.has(e.toLowerCase()))];
};

const PRIORITY_INFO = {
  critical: {
    label: 'P1 — Critical',
    color: '#dc2626',
    bg: '#fee2e2',
    sla: '15 min response · 4 hr resolution',
  },
  high: {
    label: 'P2 — High',
    color: '#d97706',
    bg: '#fef3c7',
    sla: '30 min response · 8 hr resolution',
  },
  medium: {
    label: 'P3 — Medium',
    color: '#2563eb',
    bg: '#dbeafe',
    sla: '2 hr response · 24 hr resolution',
  },
  low: {
    label: 'P4 — Low',
    color: '#059669',
    bg: '#d1fae5',
    sla: '4 hr response · 48 hr resolution',
  },
};

const TIER_DATA = [
  {
    id: 'L1',
    title: 'L1 — Basic Support',
    subtitle: 'General how-to, access issues, standard queries',
    color: '#2563eb',
    bg: '#eff6ff',
    border: '#bfdbfe',
  },
  {
    id: 'L2',
    title: 'L2 — Functional Support',
    subtitle: 'Module-level bugs, config issues, workflow problems',
    color: '#7c3aed',
    bg: '#f5f3ff',
    border: '#ddd6fe',
  },
  {
    id: 'L3',
    title: 'L3 — Technical Support',
    subtitle: 'Integrations, data issues, performance, scripts',
    color: '#d97706',
    bg: '#fffbeb',
    border: '#fde68a',
  },
  {
    id: 'L4',
    title: 'L4 — Critical / Escalation',
    subtitle: 'System down, data loss, security breach, custom dev',
    color: '#dc2626',
    bg: '#fff1f2',
    border: '#fecdd3',
  },
];

const HELP_TIER_EXAMPLES = {
  L1: [
    'How do I generate a salary slip?',
    'I cannot log in to the ERP portal',
    'How to add a new vendor in the system?',
    'Password reset request',
    'User account creation / role assignment',
  ],
  L2: [
    'Invoice posting is failing with error XYZ',
    'Attendance not syncing with payroll',
    'Sales order workflow stuck in approval',
    'Wrong tax calculation on purchase invoice',
    'Report showing incorrect stock balance',
  ],
  L3: [
    'API integration with third-party app broken',
    'Batch job failing overnight — data not processed',
    'Database query taking 10+ minutes to execute',
    'Custom script throwing runtime exception',
    'Data migration validation errors',
  ],
  L4: [
    'Production server completely down',
    'Critical data deleted accidentally',
    'Security breach / unauthorized access detected',
    'Payroll processing blocked — payday tomorrow',
    'Complete ERP module non-functional for all users',
  ],
};

const REQUEST_TYPES = [
  { value: 'incident',        label: 'Incident'        },
  { value: 'service_request', label: 'Service Request' },
  { value: 'change_request',  label: 'Change Request'  },
];

const IMPACT_OPTIONS = [
  { value: 'high', label: 'High — Business-wide / multiple departments' },
  { value: 'medium', label: 'Medium — Single department / team' },
  { value: 'low', label: 'Low — Individual user' },
];

const URGENCY_OPTIONS = [
  { value: 'high', label: 'High — Immediate action required' },
  { value: 'medium', label: 'Medium — Work affected but workaround exists' },
  { value: 'low', label: 'Low — Minor inconvenience' },
];

const PRIORITY_OVERRIDE_OPTIONS = [
  { value: '', label: '— Use auto-calculated priority —' },
  { value: 'critical', label: 'P1 — Critical' },
  { value: 'high', label: 'P2 — High' },
  { value: 'medium', label: 'P3 — Medium' },
  { value: 'low', label: 'P4 — Low' },
];

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

const labelStyle = {
  display: 'block',
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 5,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const sectionTitleStyle = {
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 14,
  paddingBottom: 8,
  borderBottom: '1px solid var(--border)',
};

// ---------------------------------------------------------------------------
// HelpPanel
// ---------------------------------------------------------------------------

function HelpPanel({ onClose }) {
  const matrixRows = [
    ['', 'High Urgency', 'Medium Urgency', 'Low Urgency'],
    ['High Impact', 'P1 — Critical', 'P2 — High', 'P3 — Medium'],
    ['Medium Impact', 'P2 — High', 'P3 — Medium', 'P4 — Low'],
    ['Low Impact', 'P3 — Medium', 'P4 — Low', 'P4 — Low'],
  ];

  const cellStyle = (cell) => {
    if (cell.includes('P1')) return { background: '#fee2e2', color: '#dc2626', fontWeight: 700 };
    if (cell.includes('P2')) return { background: '#fef3c7', color: '#d97706', fontWeight: 700 };
    if (cell.includes('P3')) return { background: '#dbeafe', color: '#2563eb', fontWeight: 700 };
    if (cell.includes('P4')) return { background: '#d1fae5', color: '#059669', fontWeight: 700 };
    return { background: 'var(--bg-secondary, #f8fafc)', color: 'var(--text-secondary)', fontWeight: 700 };
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.38)',
          zIndex: 1000,
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 430,
          height: '100vh',
          background: 'var(--bg-card)',
          boxShadow: '-6px 0 36px rgba(0,0,0,0.18)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px',
            borderBottom: '1px solid var(--border)',
            position: 'sticky',
            top: 0,
            background: 'var(--bg-card)',
            zIndex: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HelpCircle size={18} color="var(--accent, #2563eb)" />
            <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-main)' }}>
              Ticket Help Guide
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 6,
              borderRadius: 6,
              color: 'var(--text-secondary)',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Tier Descriptions */}
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--text-main)',
                marginBottom: 14,
              }}
            >
              Support Tier Definitions &amp; Examples
            </div>
            {TIER_DATA.map((tier) => (
              <div
                key={tier.id}
                style={{
                  border: `1.5px solid ${tier.border}`,
                  borderRadius: 10,
                  padding: '12px 14px',
                  marginBottom: 10,
                  background: tier.bg,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                  <span
                    style={{
                      background: tier.color,
                      color: '#fff',
                      borderRadius: 5,
                      padding: '2px 9px',
                      fontSize: 11.5,
                      fontWeight: 700,
                    }}
                  >
                    {tier.id}
                  </span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: tier.color }}>
                    {tier.title.split('—')[1]?.trim()}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
                  {tier.subtitle}
                </div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {HELP_TIER_EXAMPLES[tier.id].map((ex, i) => (
                    <li
                      key={i}
                      style={{ fontSize: 12, color: '#444', marginBottom: 3, lineHeight: 1.5 }}
                    >
                      {ex}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Priority Matrix */}
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--text-main)',
                marginBottom: 12,
              }}
            >
              Priority Matrix
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {matrixRows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        style={{
                          padding: '7px 9px',
                          border: '1px solid var(--border)',
                          textAlign: 'center',
                          fontSize: ri === 0 || ci === 0 ? 11.5 : 12,
                          ...cellStyle(cell),
                        }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* SLA Targets */}
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--text-main)',
                marginBottom: 12,
              }}
            >
              SLA Targets
            </div>
            {Object.entries(PRIORITY_INFO).map(([key, info]) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: info.bg,
                  border: `1px solid ${info.color}35`,
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    background: info.color,
                    color: '#fff',
                    borderRadius: 5,
                    padding: '3px 10px',
                    fontSize: 11.5,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    minWidth: 72,
                    textAlign: 'center',
                  }}
                >
                  {info.label.split('—')[0].trim()}
                </span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 12, color: info.color }}>
                    {info.label.split('—')[1]?.trim()}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#666', marginTop: 2 }}>{info.sla}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// EmailChips
// ---------------------------------------------------------------------------

function EmailChips({ emails, onChange }) {
  const [input, setInput] = useState('');

  const addEmail = (raw) => {
    const val = raw.trim().replace(/,$/, '').toLowerCase();
    if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && !emails.includes(val)) {
      onChange([...emails, val]);
    }
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addEmail(input);
    } else if (e.key === 'Backspace' && !input && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        padding: '7px 10px',
        border: '1.5px solid var(--border)',
        borderRadius: 8,
        background: 'var(--bg-input)',
        minHeight: 42,
        alignItems: 'center',
        cursor: 'text',
        boxSizing: 'border-box',
      }}
      onClick={(e) => e.currentTarget.querySelector('input')?.focus()}
    >
      {emails.map((email) => (
        <span
          key={email}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'var(--accent, #2563eb)',
            color: '#fff',
            borderRadius: 20,
            padding: '2px 10px',
            fontSize: 12,
            fontWeight: 500,
          }}
        >
          {email}
          <button
            type="button"
            onClick={() => onChange(emails.filter((e) => e !== email))}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#fff',
              padding: 0,
              lineHeight: 1,
              fontSize: 15,
              opacity: 0.85,
              marginLeft: 1,
            }}
          >
            ×
          </button>
        </span>
      ))}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) addEmail(input); }}
        placeholder={emails.length === 0 ? 'Type email and press Enter...' : ''}
        style={{
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: 'var(--text-main)',
          fontSize: 13.5,
          fontFamily: 'inherit',
          flex: 1,
          minWidth: 160,
          padding: '2px 4px',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// NewTicketPage
// ---------------------------------------------------------------------------

export default function NewTicketPage({ editTicket = null, embedded = false, onClose = null, onUpdated = null }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEdit = !!editTicket;

  const [showHelp, setShowHelp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Remote data
  const [projects,    setProjects]    = useState([]);
  const [allConsultants, setAllConsultants] = useState([]); // full list
  const [consultants, setConsultants] = useState([]); // filtered by project
  const [subProjects, setSubProjects] = useState([]);
  const [users,       setUsers]       = useState([]);

  // Requestor (admin only — ticket on behalf of a user)
  const [requestorId, setRequestorId] = useState('');
  // Requestor pre-filled from the ERP "Raise ITSM Ticket" flow (matched by
  // email). Kept separately so it can be shown even if the matched user is
  // not in the role='user' dropdown list.
  const [prefillRequestor, setPrefillRequestor] = useState(null);

  // Tier
  const [tier, setTier] = useState('');

  // Priority
  const [impact, setImpact] = useState('');
  const [urgency, setUrgency] = useState('');
  const [priorityOverride, setPriorityOverride] = useState('');

  // Classification
  const [requestType, setRequestType] = useState('');
  const [erpSystem, setErpSystem] = useState('');
  const [module, setModule] = useState('');
  const [customModule, setCustomModule] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [customSubcategory, setCustomSubcategory] = useState('');
  const [erpVersion, setErpVersion] = useState('');
  const [sapTCode, setSapTCode] = useState('');
  const [site, setSite] = useState('');

  // Details
  const [contactNumber, setContactNumber] = useState('');
  const [affectedUsers, setAffectedUsers] = useState('');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState(DESCRIPTION_TEMPLATE);
  // { blob, url, mimeType, durationSeconds } while a clip is held, else null.
  const [voiceClip, setVoiceClip] = useState(null);

  // Assignment
  const [consultantId,  setConsultantId]  = useState('');
  const [groups,        setGroups]        = useState([]);
  const [groupFilter,   setGroupFilter]   = useState(''); // selected group → filters consultants
  const [projectId,     setProjectId]     = useState('');
  const [subProjectId,  setSubProjectId]  = useState('');
  const [ccEmails,      setCcEmails]      = useState(DEFAULT_CC_EMAILS);

  // Pre-fill project from query params
  useEffect(() => {
    const p = searchParams.get('project');
    if (p) setProjectId(p);
  }, [searchParams]);

  // Pre-fill subject from query params (used by the ERP "Raise ITSM Ticket"
  // flow, which passes the error message through as ?subject=...).
  useEffect(() => {
    const s = searchParams.get('subject');
    if (s) setSubject(s);
  }, [searchParams]);

  // Edit mode: pre-fill the whole form from the existing ticket.
  useEffect(() => {
    if (!editTicket) return;
    const t = editTicket;
    setSubject(t.title || '');
    setDescription(t.description || DESCRIPTION_TEMPLATE);
    setRequestType(t.ticket_type || '');
    setImpact(t.impact || '');
    setUrgency(t.urgency || '');
    setTier(t.support_tier || '');
    const auto = t.impact && t.urgency ? (PRIORITY_MATRIX[t.impact]?.[t.urgency] || '') : '';
    setPriorityOverride(t.priority && t.priority !== auto ? t.priority : '');
    setErpSystem(t.service_category || '');
    setModule(t.category || '');
    setSubcategory(t.subcategory || '');
    setErpVersion(t.erp_version || '');
    setSapTCode(t.sap_transaction_code || '');
    setSite(t.site || '');
    setContactNumber(t.contact_number || '');
    setAffectedUsers(t.affected_users_count != null ? String(t.affected_users_count) : '');
    setRequestorId(t.requestor_id || '');
    if (t.requestor_id) setPrefillRequestor({ id: t.requestor_id, name: t.requestor_name, email: t.requestor_email });
    setConsultantId(t.assigned_to || '');
    setGroupFilter(t.group_id || '');
    setProjectId(t.project_id || '');
    setSubProjectId(t.sub_project_id || '');
    setCcEmails(withDefaultCc(t.notify_emails ? t.notify_emails.split(',').map(s => s.trim()).filter(Boolean) : []));
  }, [editTicket]);

  // Load remote lists
  useEffect(() => {
    getProjects().then((res) => setProjects(res.data || [])).catch(() => {});
    getGroups().then((res) => setGroups(res.data || [])).catch(() => {});
    getConsultants().then((res) => {
      setAllConsultants(res.data || []);
      setConsultants(res.data || []);
    }).catch(() => {});
    if (user?.role === 'admin') {
      getAllUsers().then((res) => {
        setUsers((res.data || []).filter(u => u.role === 'user'));
      }).catch(() => {});

      // Resolve the requestor passed from the ERP. ensureRequestor finds the
      // matching ITSM user by email, or creates one on the fly — so any ERP
      // user (including brand-new ones) is auto-selected here without a sync.
      const reqEmail = (searchParams.get('requestorEmail') || '').trim();
      const reqName = (searchParams.get('requestorName') || '').trim();
      if (reqEmail) {
        ensureRequestor({ email: reqEmail, name: reqName || reqEmail })
          .then((res) => {
            const u = res.data?.user;
            if (u?.id) { setRequestorId(u.id); setPrefillRequestor(u); }
          })
          .catch(() => {});
      }
    }
  }, [user?.role, searchParams]);

  // When project changes → load sub-projects + filter consultants to project members
  useEffect(() => {
    setSubProjectId('');
    setConsultantId('');
    if (!projectId) {
      setSubProjects([]);
      setConsultants(allConsultants);
      return;
    }
    // Fetch sub-projects for this project
    getSubProjects({ project_id: projectId })
      .then(r => setSubProjects(r.data || []))
      .catch(() => setSubProjects([]));
    // Filter consultants to project members only
    getProjectMembers(projectId)
      .then(r => {
        const memberIds = new Set((r.data || []).map(m => m.id));
        const filtered = allConsultants.filter(c => memberIds.has(c.id || c._id));
        setConsultants(filtered.length > 0 ? filtered : allConsultants);
      })
      .catch(() => setConsultants(allConsultants));
  }, [projectId]); // eslint-disable-line

  // Derived
  const autoPriority = impact && urgency ? (PRIORITY_MATRIX[impact]?.[urgency] || '') : '';
  const effectivePriority = priorityOverride || autoPriority;
  const priorityInfo = effectivePriority ? PRIORITY_INFO[effectivePriority] : null;

  const modules = erpSystem ? Object.keys(CATMAP[erpSystem] || {}) : [];
  const subcategories = erpSystem && module ? (CATMAP[erpSystem]?.[module] || []) : [];
  const showErpVersion = ERP_SYSTEMS.includes(erpSystem);
  const showSapTCode = erpSystem === 'SAP ERP';
  const erpVersionOptions = erpSystem === 'SAP ERP' ? SAP_VERSIONS : (ERP_VERSIONS[erpSystem] || []);

  const handleErpSystemChange = (val) => {
    setErpSystem(val);
    setModule('');
    setCustomModule('');
    setSubcategory('');
    setCustomSubcategory('');
    setErpVersion('');
    setSapTCode('');
  };

  const handleModuleChange = (val) => {
    setModule(val);
    setCustomModule('');
    setSubcategory('');
    setCustomSubcategory('');
  };

  const handleReset = () => {
    setTier('');
    setImpact('');
    setUrgency('');
    setPriorityOverride('');
    setRequestType('');
    setErpSystem('');
    setModule('');
    setCustomModule('');
    setSubcategory('');
    setCustomSubcategory('');
    setErpVersion('');
    setSapTCode('');
    setSite('');
    setContactNumber('');
    setAffectedUsers('');
    setSubject('');
    setDescription(DESCRIPTION_TEMPLATE);
    setRequestorId('');
    setConsultantId('');
    setProjectId('');
    setSubProjectId('');
    setCcEmails(DEFAULT_CC_EMAILS);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!subject.trim()) { setError('Subject is required.'); return; }
    if (!description.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim()) { setError('Description is required.'); return; }
    if (!requestType) { setError('Request Type is required.'); return; }
    if (!erpSystem) { setError('ERP / Service System is required.'); return; }
    if (!effectivePriority) {
      setError('Please select Impact and Urgency to determine priority.');
      return;
    }

    const payload = {
      /* ── fields the backend expects ── */
      title:                subject,
      description:          description,
      ticket_type:          requestType,
      priority:             effectivePriority,
      support_tier:         tier         || null,
      impact:               impact       || null,
      urgency:              urgency      || null,
      service_category:     erpSystem    || null,
      category:             (module === 'Other' ? customModule : module) || null,
      subcategory:          (subcategory === 'Other' ? customSubcategory : subcategory) || null,
      erp_version:          erpVersion   || null,
      sap_transaction_code: sapTCode     || null,
      site:                 site         || null,
      contact_number:       contactNumber || null,
      affected_users_count: affectedUsers ? parseInt(affectedUsers) : null,
      requestor_id:         requestorId  || null,
      assigned_to:          consultantId || null,
      project_id:           projectId    || null,
      sub_project_id:       subProjectId || null,
      notify_emails:        ccEmails.length ? ccEmails.join(',') : null,
    };

    try {
      setSubmitting(true);
      if (isEdit) {
        await updateTicket(editTicket.id, payload);
        if (onUpdated) onUpdated();
        return;
      }
      const res = await createTicket(payload);
      /* backend returns the new ticket object as res.data */
      const id = res?.data?.id || res?.data?._id;

      // Attach the voice message once the ticket exists. A failure here must
      // not lose the ticket the user just filled in — it is already saved — so
      // we surface a warning on the ticket page instead of throwing.
      let voiceFailed = false;
      if (id && voiceClip?.blob) {
        try {
          const ext = (voiceClip.mimeType || '').includes('mp4') ? 'm4a'
            : (voiceClip.mimeType || '').includes('ogg') ? 'ogg' : 'webm';
          const fd = new FormData();
          // Text fields must precede the file: multer only exposes fields it
          // has already parsed when the file handler runs.
          fd.append('is_voice_note', 'true');
          fd.append('duration_seconds', String(voiceClip.durationSeconds || 0));
          fd.append('file', voiceClip.blob, `voice-message.${ext}`);
          await uploadAttachment(id, fd);
        } catch {
          voiceFailed = true;
        }
      }

      navigate(id ? `/tickets/${id}` : '/tickets',
        voiceFailed ? { state: { voiceUploadFailed: true } } : undefined);
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        `Failed to ${isEdit ? 'update' : 'create'} ticket. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const Wrapper = embedded ? React.Fragment : AppLayout;
  const wrapperProps = embedded ? {} : { pageTitle: 'New Request' };

  return (
    <Wrapper {...wrapperProps}>
      {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 0 48px' }}>
        {/* Page header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 22,
          }}
        >
          <div>
            <div style={{ fontSize: 21, fontWeight: 700, color: 'var(--text-main)' }}>
              {isEdit ? 'Update Ticket' : 'Create New Ticket'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              {isEdit ? 'Edit the details below and save your changes.' : 'Fill in the details below to submit a support request.'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '8px 16px',
              border: '1.5px solid var(--accent, #2563eb)',
              borderRadius: 8,
              background: 'transparent',
              color: 'var(--accent, #2563eb)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              flexShrink: 0,
            }}
          >
            <HelpCircle size={15} />
            Help Guide
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate>

          {/* ── Priority Assessment ── */}
          <div className="card" style={{ padding: '18px 20px', marginBottom: 12 }}>
            <div style={sectionTitleStyle}>Priority Assessment</div>

            {/* Impact + Urgency */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 14,
                marginBottom: 14,
              }}
            >
              <div>
                <label style={labelStyle}>Impact *</label>
                <Select
                  value={impact}
                  onChange={(v) => { setImpact(v); setPriorityOverride(''); }}
                  options={IMPACT_OPTIONS}
                  placeholder="Select impact..."
                />
              </div>
              <div>
                <label style={labelStyle}>Urgency *</label>
                <Select
                  value={urgency}
                  onChange={(v) => { setUrgency(v); setPriorityOverride(''); }}
                  options={URGENCY_OPTIONS}
                  placeholder="Select urgency..."
                />
              </div>
            </div>

            {/* Support Tier — compact pill row below Impact/Urgency */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Support Tier</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {TIER_DATA.map((t) => {
                  const selected = tier === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTier(selected ? '' : t.id)}
                      style={{
                        padding: '6px 20px',
                        borderRadius: 8,
                        border: `1.5px solid ${selected ? t.color : 'var(--border)'}`,
                        background: selected ? t.color : 'var(--bg-card)',
                        color: selected ? '#fff' : 'var(--text-secondary)',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        fontFamily: 'inherit',
                        fontWeight: 700,
                        fontSize: 13,
                        userSelect: 'none',
                      }}
                    >
                      {t.id}
                    </button>
                  );
                })}
              </div>
              {tier && (
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 6 }}>
                  {TIER_DATA.find(t => t.id === tier)?.subtitle}
                </div>
              )}
            </div>

            {/* Auto-priority box */}
            {priorityInfo && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 15px',
                  borderRadius: 9,
                  background: priorityInfo.bg,
                  border: `1.5px solid ${priorityInfo.color}45`,
                  marginBottom: 14,
                }}
              >
                <span
                  style={{
                    background: priorityInfo.color,
                    color: '#fff',
                    borderRadius: 6,
                    padding: '4px 13px',
                    fontWeight: 700,
                    fontSize: 13,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {priorityInfo.label}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {priorityOverride && (
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: priorityInfo.color,
                        background: `${priorityInfo.color}20`,
                        borderRadius: 4,
                        padding: '2px 7px',
                      }}
                    >
                      OVERRIDDEN
                    </span>
                  )}
                  <span
                    style={{ fontSize: 12.5, color: priorityInfo.color, fontWeight: 500 }}
                  >
                    {priorityInfo.sla}
                  </span>
                </div>
              </div>
            )}

            {/* Override */}
            <div>
              <label style={labelStyle}>Override Priority</label>
              <Select
                value={priorityOverride}
                onChange={setPriorityOverride}
                options={PRIORITY_OVERRIDE_OPTIONS}
              />
            </div>
          </div>

          {/* ── Ticket Classification ── */}
          <div className="card" style={{ padding: '18px 20px', marginBottom: 12 }}>
            <div style={sectionTitleStyle}>Ticket Classification</div>

            <div
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}
            >
              <div>
                <label style={labelStyle}>Request Type *</label>
                <Select
                  value={requestType}
                  onChange={setRequestType}
                  options={REQUEST_TYPES}
                  placeholder="— Select Request Type —"
                />
              </div>
              <div>
                <label style={labelStyle}>ERP / Service System *</label>
                <Select
                  value={erpSystem}
                  onChange={handleErpSystemChange}
                  options={Object.keys(CATMAP)}
                  placeholder="Select system..."
                />
              </div>
            </div>

            <div
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}
            >
              <div>
                <label style={labelStyle}>Module / Category</label>
                <Select
                  value={module}
                  onChange={handleModuleChange}
                  disabled={!erpSystem}
                  options={[...modules, 'Other']}
                  placeholder="Select module..."
                />
                {module === 'Other' && (
                  <input
                    type="text"
                    value={customModule}
                    onChange={(e) => setCustomModule(e.target.value)}
                    placeholder="Enter module / category name..."
                    className="form-control" style={{ marginTop: 8 }}
                    autoFocus
                  />
                )}
              </div>
              <div>
                <label style={labelStyle}>Subcategory</label>
                <Select
                  value={subcategory}
                  onChange={setSubcategory}
                  disabled={!module}
                  options={module ? [...subcategories, 'Other'] : subcategories}
                  placeholder="Select subcategory..."
                />
                {subcategory === 'Other' && (
                  <input
                    type="text"
                    value={customSubcategory}
                    onChange={(e) => setCustomSubcategory(e.target.value)}
                    placeholder="Enter subcategory name..."
                    className="form-control" style={{ marginTop: 8 }}
                    autoFocus
                  />
                )}
              </div>
            </div>

            {/* Conditional row: ERP version / SAP T-Code / Site */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${showErpVersion || showSapTCode ? 3 : 1}, 1fr)`,
                gap: 14,
              }}
            >
              {showErpVersion && (
                <div>
                  <label style={labelStyle}>ERP Version</label>
                  <Select
                    value={erpVersion}
                    onChange={setErpVersion}
                    options={erpVersionOptions}
                    placeholder="Select version..."
                  />
                </div>
              )}
              {showSapTCode && (
                <div>
                  <label style={labelStyle}>SAP T-Code</label>
                  <input
                    type="text"
                    value={sapTCode}
                    onChange={(e) => setSapTCode(e.target.value)}
                    placeholder="e.g. FB60, ME21N, VA01"
                    className="form-control"
                  />
                </div>
              )}
              <div>
                <label style={labelStyle}>Site / Location</label>
                <input
                  type="text"
                  value={site}
                  onChange={(e) => setSite(e.target.value)}
                  placeholder="e.g. Head Office, Plant 1"
                  className="form-control"
                />
              </div>
            </div>
          </div>

          {/* ── Ticket Details ── */}
          <div className="card" style={{ padding: '18px 20px', marginBottom: 12 }}>
            <div style={sectionTitleStyle}>Ticket Details</div>

            <div
              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}
            >
              <div>
                <label style={labelStyle}>Contact Number</label>
                <input
                  type="tel"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  placeholder="+91 98765 43210"
                  className="form-control"
                />
              </div>
              <div>
                <label style={labelStyle}>Affected Users</label>
                <input
                  type="text"
                  value={affectedUsers}
                  onChange={(e) => setAffectedUsers(e.target.value)}
                  placeholder="e.g. 5, All finance team, Everyone"
                  className="form-control"
                />
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Subject *</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief, clear summary of the issue"
                className="form-control"
              />
            </div>

            <div>
              <label style={labelStyle}>Description *</label>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                author={user?.name || 'You'}
                placeholder="Describe the issue in detail…"
              />
            </div>

            {/* Voice message — optional. Uploaded after the ticket exists, so
                it needs a ticket id; see handleSubmit. Hidden while editing,
                where the recording already lives on the ticket. */}
            {!isEdit && (
              <div style={{ marginTop: 14 }}>
                <label style={labelStyle}>Voice Message</label>
                <VoiceRecorder onChange={setVoiceClip} maxSeconds={180} disabled={submitting} />
              </div>
            )}
          </div>

          {/* ── Assignment & Notification ── */}
          <div className="card" style={{ padding: '18px 20px', marginBottom: 12 }}>
            <div style={sectionTitleStyle}>Assignment &amp; Notification</div>

            {/* Requestor — admin only: ticket raised on behalf of a user */}
            {user?.role === 'admin' && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>
                  Requestor
                  <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--text-secondary)', marginLeft: 6, fontSize: 11 }}>
                    — who is this ticket for?
                  </span>
                </label>
                <Select
                  value={requestorId}
                  onChange={setRequestorId}
                  options={[
                    { value: '', label: '— Select user (leave blank if self) —' },
                    // Requestor passed in from the ERP, if not already in the list.
                    ...(prefillRequestor && !users.some(u => u.id === prefillRequestor.id)
                      ? [{ value: prefillRequestor.id, label: `${prefillRequestor.name}${prefillRequestor.department ? ` · ${prefillRequestor.department}` : ''}${prefillRequestor.email ? ` (${prefillRequestor.email})` : ''}` }]
                      : []),
                    ...users.map((u) => ({ value: u.id, label: `${u.name}${u.department ? ` · ${u.department}` : ''}${u.email ? ` (${u.email})` : ''}` })),
                  ]}
                />
                {requestorId && (
                  <div style={{ marginTop: 6, padding: '7px 12px', borderRadius: 7, background: '#f0fdf4', border: '1px solid #86efac', fontSize: 12, color: '#166534', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>✓</span>
                    <span>Ticket will be created on behalf of <strong>{(users.find(u => u.id === requestorId) || prefillRequestor)?.name}</strong>. They will be notified when resolved.</span>
                  </div>
                )}
              </div>
            )}

            {/* Row 1: Project + Sub-Project */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Client</label>
                <Select
                  value={projectId}
                  onChange={setProjectId}
                  options={[{ value: '', label: '— No client —' }, ...projects.map((p) => ({ value: p.id, label: p.name }))]}
                />
              </div>
              <div>
                <label style={labelStyle}>Project</label>
                <Select
                  value={subProjectId}
                  onChange={setSubProjectId}
                  disabled={!projectId || subProjects.length === 0}
                  options={[{ value: '', label: '— No project —' }, ...subProjects.map((sp) => ({ value: sp.id, label: sp.name }))]}
                />
                {projectId && subProjects.length === 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    No projects for this client yet.
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Group + Assign Consultant — pick a group first, then a consultant in it */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Group / Module</label>
                <Select
                  value={groupFilter}
                  onChange={(v) => { setGroupFilter(v); setConsultantId(''); }}
                  options={[{ value: '', label: '— All groups —' }, ...groups.map((g) => ({ value: g.id, label: `${g.name}${g.consultant_count != null ? ` (${g.consultant_count})` : ''}` }))]}
                />
              </div>
              <div>
                <label style={labelStyle}>
                  Assign Consultant
                  {projectId && consultants.length < allConsultants.length && (
                    <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'var(--primary)', marginLeft: 8, fontSize: 11 }}>
                      · project members
                    </span>
                  )}
                </label>
                <Select
                  value={consultantId}
                  onChange={setConsultantId}
                  options={[
                    { value: '', label: '— Unassigned —' },
                    ...consultants
                      .filter((c) => !groupFilter || c.group_id === groupFilter)
                      .map((c) => ({ value: c._id || c.id, label: `${c.name || c.fullName || c.email}${c.group_code ? ` · ${c.group_code}` : ''}` })),
                  ]}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Email Notifications (CC)</label>
              <EmailChips emails={ccEmails} onChange={setCcEmails} />
              <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 5 }}>
                Type an email address and press Enter, comma, or Space to add.
              </div>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div
              style={{
                background: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: 8,
                padding: '11px 16px',
                marginBottom: 14,
                color: '#dc2626',
                fontSize: 13.5,
                fontWeight: 500,
              }}
            >
              {error}
            </div>
          )}

          {/* Footer actions */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 10,
              paddingTop: 4,
            }}
          >
            <button
              type="button"
              onClick={() => (embedded && onClose ? onClose() : navigate(-1))}
              style={{
                padding: '9px 20px',
                border: '1.5px solid var(--border)',
                borderRadius: 8,
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: 13.5,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleReset}
              style={{
                padding: '9px 20px',
                border: '1.5px solid var(--border)',
                borderRadius: 8,
                background: 'transparent',
                color: 'var(--text-main)',
                fontSize: 13.5,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Reset
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '9px 28px',
                border: 'none',
                borderRadius: 8,
                background: submitting ? '#93c5fd' : 'var(--accent, #2563eb)',
                color: '#fff',
                fontSize: 13.5,
                fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                letterSpacing: '0.01em',
              }}
            >
              {submitting ? (isEdit ? 'Updating...' : 'Submitting...') : (isEdit ? 'Update Ticket' : 'Submit Ticket')}
            </button>
          </div>
        </form>
      </div>
    </Wrapper>
  );
}
